import { Mimshak, PolicyOrAccount, PolicyStatus, Product } from "../interfaces/DTO/mislaka_xml_models";
import { ManualMimshakData } from "../interfaces/DTO/manualMimshakData";
import { MortgageDetails } from "../interfaces/DTO/mortgage/mortgage";

/**
 * Mortgage Mimshak Builder
 * =========================
 *
 * יוצר מבנה Mimshak רזה למשכנתאות שמשתמש בשדה data של ManualMimshakData.
 *
 * המבנה:
 * Mimshak -> Product -> PolicyOrAccount -> mortgage_details
 *
 * דוגמת שימוש:
 * ```typescript
 * const mortgageData: ManualMimshakData = {
 *   product_type: ManualProductType.MORTGAGE,
 *   handler_code: "12",
 *   policy_number: "MORTGAGE123456",
 *   funds_sum: 800000, // יתרת משכנתא
 *   withdrawal_amount: 4500, // החזר חודשי
 *   join_date: "2023-01-01",
 *   // ... שדות נוספים
 * };
 *
 * const mimshak = buildMortgageMimshak(mortgageData);
 * // השדה data של ManualMimshakData יכיל את ה-mimshak המקודד
 * ```
 */

/**
 * יוצר אובייקט MortgageDetails מתוך ManualMimshakData
 *
 * אם יש data.products[0].policies_or_accounts[0].mortgage_details קיים - משתמש בו
 * אחרת - בונה מהשדות הבסיסיים
 *
 * @param mortgageData - נתוני המשכנתא הידנית
 * @returns אובייקט MortgageDetails עם הנתונים המלאים
 */
export const buildMortgageDetails = (mortgageData: ManualMimshakData): MortgageDetails => {
  // אם יש data עם mortgage_details - השתמש בו (קח את האלמנט הראשון במערך)
  const existingMortgageDetails = (mortgageData.data as any)?.products?.[0]?.policies_or_accounts?.[0]?.mortgage_details?.[0];

  if (existingMortgageDetails) {
    return existingMortgageDetails;
  }

  // אחרת - בנה מהשדות הבסיסיים עם ערכי ברירת מחדל
  return {
    mortgage_number: mortgageData.policy_number,
   
    total_current_balance: mortgageData.funds_sum || 0, // יתרת המשכנתא הנוכחית
    monthly_payment_total: mortgageData.withdrawal_amount || 0, // החזר חודשי כולל
    start_date: mortgageData.join_date || new Date().toISOString().split('T')[0], // תאריך התחלת המשכנתא - היום אם לא הוזן
    bank_code: mortgageData.handler_code,
    tracks: [], // מסלולים יתווספו בעריכה במחשבון המשכנתא
  };
};

/**
 * יוצר אובייקט PolicyOrAccount רזה למשכנתא
 */
export const buildMortgagePolicyOrAccount = (mortgageData: ManualMimshakData): PolicyOrAccount => {
  return {
    policy_number: mortgageData.policy_number,
    status: PolicyStatus.ACTIVE, // סטטוס פעיל
    update_date: mortgageData.reported_period, // תאריך עדכון - התקופה המדווחת
    program_name: "משכנתא", // שם התוכנית
    investment_paths: [], // רשימה רזה - למשכנתא אין מסלולי השקעה
    join_date: mortgageData.join_date || undefined, // תאריך הצטרפות
    // שמירת mortgage_details כמערך - דומה לאופן שבו loan_details מאוחסן
    mortgage_details: [buildMortgageDetails(mortgageData)] as any
  } as any;
};

/**
 * יוצר אובייקט Product רזה למשכנתא
 */
export const buildMortgageProduct = (mortgageData: ManualMimshakData): Product => {
  return {
    details: {
      producer_code: "",
      handler_code: mortgageData.handler_code,
      product_type: mortgageData.product_type,
    },
    policies_or_accounts: [buildMortgagePolicyOrAccount(mortgageData)],
  };
};

/**
 * יוצר אובייקט Mimshak רזה למשכנתא
 */
export const buildMortgageMimshak = (mortgageData: ManualMimshakData): Mimshak => {
  return {
    products: [buildMortgageProduct(mortgageData)],
  };
};
